import { useState } from 'react'
import { Clock, Fingerprint } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useApprovalMutations, useRegularizationContext } from '../api'
import { PUNCH_STATES, REGULARIZATION_TYPE_LABELS } from '../types'

/**
 * Deciding on a correction, with the evidence next to the claim.
 *
 * The punches the terminals actually recorded are the whole point of this screen. Approving
 * a time somebody typed without seeing what the hardware saw is a rubber stamp, and a single
 * 08:57 with no exit reads very differently from four punches with a gap in the middle.
 */
export function RegularisationApproval({
  requestId,
  onDecided,
}: {
  requestId: string
  onDecided?: () => void
}) {
  const { data: context, isPending } = useRegularizationContext(requestId)
  const { approve, reject } = useApprovalMutations()
  const [comment, setComment] = useState('')
  const [error, setError] = useState<string | null>(null)

  if (isPending) {
    return <p className="text-sm text-[var(--color-muted)]">Loading the day…</p>
  }
  if (!context) {
    return <p className="text-sm text-[var(--color-muted)]">That correction could not be loaded.</p>
  }

  const { request, punches, myTaskId } = context

  async function decide(action: 'approve' | 'reject') {
    if (!myTaskId) return
    setError(null)
    try {
      const run = action === 'approve' ? approve : reject
      await run.mutateAsync({ taskId: myTaskId, comment })
      onDecided?.()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That decision could not be recorded')
    }
  }

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <h3 className="text-base font-semibold text-[var(--color-heading)]">
          {request.employeeName} — {new Date(context.workDate).toLocaleDateString()}
        </h3>
        <p className="text-sm text-[var(--color-muted)]">
          {REGULARIZATION_TYPE_LABELS[request.requestType]}
          {request.origin === 'SYSTEM' && ' · raised automatically'}
          {context.shiftName && ` · ${context.shiftName}`}
          {context.holiday && ' · public holiday'}
          {context.weekOff && ' · rest day'}
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        <section className="rounded-[var(--radius-control)] border border-[var(--color-border)] p-3">
          <h4 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-[var(--color-heading)]">
            <Clock className="size-4 text-[var(--color-muted)]" aria-hidden="true" />
            Being claimed
          </h4>
          <dl className="space-y-1 text-sm">
            <Row label="Time in" value={formatTime(request.requestedFirstIn)} />
            <Row label="Time out" value={formatTime(request.requestedLastOut)} />
            <Row label="Day currently reads" value={`${context.currentStatus ?? '—'}, ${context.currentWorkedMinutes ?? 0} min`} />
          </dl>
          {request.reason && (
            <p className="mt-2 border-t border-[var(--color-border)] pt-2 text-sm text-[var(--color-body)]">
              {request.reason}
            </p>
          )}
        </section>

        <section className="rounded-[var(--radius-control)] border border-[var(--color-border)] p-3">
          <h4 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-[var(--color-heading)]">
            <Fingerprint className="size-4 text-[var(--color-muted)]" aria-hidden="true" />
            What the terminals recorded
          </h4>
          {punches.length === 0 ? (
            <p className="text-sm text-[var(--color-muted)]">
              No punches at all for this day.
            </p>
          ) : (
            <ul className="space-y-1 text-sm">
              {punches.map((punch, index) => (
                <li key={`${punch.punchTime}-${index}`} className="flex justify-between gap-3">
                  <span className="tabular-nums">{formatTime(punch.punchTime)}</span>
                  <span className="text-[var(--color-muted)]">
                    {punch.punchState != null ? (PUNCH_STATES[punch.punchState] ?? '—') : '—'}
                    {punch.deviceSerial && ` · ${punch.deviceSerial}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {myTaskId ? (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="decision-comment">Comment</Label>
            <Input
              id="decision-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Optional, and kept with the approval"
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-[var(--color-danger)]">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="button" onClick={() => void decide('approve')} disabled={approve.isPending}>
              Approve the correction
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => void decide('reject')}
              disabled={reject.isPending}
            >
              Reject
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[var(--color-muted)]">
          This is not waiting on you — either somebody else is the approver, or it has already
          been decided.
        </p>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-[var(--color-muted)]">{label}</dt>
      <dd className="tabular-nums text-[var(--color-heading)]">{value}</dd>
    </div>
  )
}

function formatTime(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}
